import type {
  CouponAssignmentConfig,
  JourneyContextVariableDefinition,
} from "src/types/journeys";
import { sanitizeVariableName } from "@hightouch/lib/journeys/utils";

/**
 * Pure transformation: takes a validated coupon assignment config and returns
 * the effective variable name and the list of context variable definitions
 * this node owns. No validation — that's handled by the resolver.
 */
export function buildCouponVarDefs(config: CouponAssignmentConfig): {
  effectiveVar: string;
  nodeVarDefs: JourneyContextVariableDefinition[];
} {
  if (!config.pool_model_id) {
    return { effectiveVar: "", nodeVarDefs: [] };
  }

  const effectiveVar =
    config.assignment_variable_name ||
    (config.assignment_column
      ? sanitizeVariableName(config.assignment_column)
      : "");

  if (!effectiveVar) {
    return { effectiveVar: "", nodeVarDefs: [] };
  }

  const nodeVarDefs: JourneyContextVariableDefinition[] = [
    { variable_name: effectiveVar, variable_type: "STRING" },
  ];

  for (const col of config.additional_columns ?? []) {
    if (col.context_variable_name) {
      nodeVarDefs.push({
        variable_name: col.context_variable_name,
        variable_type: col.context_variable_type,
      });
    }
  }

  return { effectiveVar, nodeVarDefs };
}

/**
 * Returns a copy of the config with variable names suffixed to avoid
 * conflicts with the original node's context variables.
 */
export function cloneCouponConfig(
  config: CouponAssignmentConfig,
  suffix = "_copy",
): CouponAssignmentConfig {
  return {
    ...config,
    assignment_variable_name: config.assignment_variable_name
      ? `${config.assignment_variable_name}${suffix}`
      : "",
    additional_columns: (config.additional_columns ?? []).map((col) => ({
      ...col,
      context_variable_name: col.context_variable_name
        ? `${col.context_variable_name}${suffix}`
        : "",
    })),
  };
}
